import React, { useState } from 'react';
import { SUBJECT_COLORS, STATUS_STYLES } from '../../lib/mockStudent';
import { cn } from '../../lib/utils';
import { getModuleVideoId, getModuleImage, getModuleOutcomes, getModuleLanguage, youtubeThumb } from '../../lib/moduleVideos';
import VideoLightbox from './VideoLightbox';
import ImageLightbox from './ImageLightbox';
import { Maximize2 } from 'lucide-react';

export default function ModuleCard({ subject, status, title, desc, duration = '35–45 min', lang }) {
  const s = SUBJECT_COLORS[subject] || SUBJECT_COLORS['Science'];
  const st = STATUS_STYLES[status] || STATUS_STYLES['Available'];
  const videoId = getModuleVideoId(title);
  const imageUrl = !videoId ? getModuleImage(title) : null;
  const outcomes = getModuleOutcomes(title);
  const displayLang = lang || getModuleLanguage(title);

  const [openVideo, setOpenVideo] = useState(false);
  const [openImage, setOpenImage] = useState(false);

  const clickable = Boolean(videoId || imageUrl);
  const openMedia = () => { if (videoId) setOpenVideo(true); else if (imageUrl) setOpenImage(true); };

  const containerProps = clickable
    ? { role: 'button', tabIndex: 0, onClick: openMedia, onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openMedia(); } }, 'data-testid': `module-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` }
    : {};

  return (
    <>
      <div className={cn('group rounded-2xl border border-canvas-200 bg-white shadow-soft overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-card', clickable && 'cursor-pointer')} {...containerProps}>
        <div className={cn('relative aspect-[16/10] grain overflow-hidden', !videoId && !imageUrl && 'bg-gradient-to-br', !videoId && !imageUrl && s.gradient)}>
          {videoId && (
            <>
              <img src={youtubeThumb(videoId, 'hqdefault')} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors" />
              <button aria-label={`Play ${title}`} onClick={(e) => { e.stopPropagation(); setOpenVideo(true); }} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 grid h-12 w-12 place-items-center rounded-full bg-white/95 text-navy shadow-soft play-glow hover:scale-110 transition-transform">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              </button>
            </>
          )}
          {imageUrl && (
            <>
              <img src={imageUrl} alt={title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" />
              <span className="absolute right-3 bottom-3 grid h-9 w-9 place-items-center rounded-full bg-white/95 text-navy shadow-soft opacity-0 group-hover:opacity-100 transition-opacity">
                <Maximize2 className="h-4 w-4" />
              </span>
            </>
          )}
          <span className={cn('absolute right-4 top-4 rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wide uppercase ring-1', st.bg, st.text, st.ring)}>{status}</span>
        </div>
        <div className="p-4">
          <div className="text-[10.5px] font-bold tracking-[0.14em] text-purple">{subject.toUpperCase()}</div>
          <div className="mt-1 text-[15px] font-bold text-navy leading-snug">{title}</div>
          {desc && <p className="mt-1 text-[12.5px] text-ink-500 leading-snug">{desc}</p>}
          <div className="mt-3 flex items-center gap-2 text-[11px] font-semibold text-ink-500">
            <span>{duration}</span>
            <span className="text-canvas-200">·</span>
            <span>{displayLang}</span>
          </div>
        </div>
      </div>

      {openVideo && videoId && (
        <VideoLightbox videoId={videoId} title={title} onClose={() => setOpenVideo(false)} />
      )}
      {openImage && imageUrl && (
        <ImageLightbox imageUrl={imageUrl} title={title} outcomes={outcomes} onClose={() => setOpenImage(false)} />
      )}
    </>
  );
}
